import { createSignal } from 'solid-js'
import type { Employee, EmployeeMembership } from '../api/endpoints/employee.api'

export const EMPLOYEE_SESSION_STORAGE_KEY = 'employee_session_state'

interface EmployeeState {
  employee: Employee | null
  memberships: EmployeeMembership[]
  isAuthenticated: boolean
  isLoading: boolean
}

interface PersistedEmployeeSession {
  employee: Employee
  memberships: EmployeeMembership[]
}

const [employeeState, setEmployeeState] = createSignal<EmployeeState>({
  employee: null,
  memberships: [],
  isAuthenticated: false,
  isLoading: true,
})

function readPersistedSession(): PersistedEmployeeSession | null {
  try {
    const raw = localStorage.getItem(EMPLOYEE_SESSION_STORAGE_KEY)
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw) as Partial<PersistedEmployeeSession>
    if (!parsed.employee) {
      return null
    }
    return { employee: parsed.employee, memberships: parsed.memberships ?? [] }
  } catch {
    return null
  }
}

function persistSession(employee: Employee, memberships: EmployeeMembership[]) {
  try {
    localStorage.setItem(EMPLOYEE_SESSION_STORAGE_KEY, JSON.stringify({ employee, memberships }))
  } catch (error) {
    console.warn('Failed to persist the employee session', error)
  }
}

function clearPersistedSession() {
  try {
    localStorage.removeItem(EMPLOYEE_SESSION_STORAGE_KEY)
  } catch (error) {
    console.warn('Failed to clear the persisted employee session', error)
  }
}

function mergeMemberships(
  memberships: EmployeeMembership[],
  incoming: EmployeeMembership
): EmployeeMembership[] {
  return [...memberships.filter((m) => m.workspace_id !== incoming.workspace_id), incoming]
}

function baselineMemberships(employee: Employee): EmployeeMembership[] {
  const inMemory = employeeState()
  if (inMemory.employee?.id === employee.id) {
    return inMemory.memberships
  }
  const persisted = readPersistedSession()
  if (persisted?.employee.id === employee.id) {
    return persisted.memberships
  }
  return []
}

export const employeeStore = {
  getState: employeeState,

  get state() {
    return employeeState()
  },

  get employee(): Employee | null {
    return employeeState().employee
  },

  get memberships(): EmployeeMembership[] {
    return employeeState().memberships
  },

  get isAuthenticated(): boolean {
    return employeeState().isAuthenticated
  },

  get isLoading(): boolean {
    return employeeState().isLoading
  },

  setSession(employee: Employee, membership?: EmployeeMembership) {
    const base = baselineMemberships(employee)
    const memberships = membership ? mergeMemberships(base, membership) : base
    setEmployeeState({ employee, memberships, isAuthenticated: true, isLoading: false })
    persistSession(employee, memberships)
  },

  replaceMemberships(employee: Employee, memberships: EmployeeMembership[]) {
    setEmployeeState({ employee, memberships, isAuthenticated: true, isLoading: false })
    persistSession(employee, memberships)
  },

  async load(): Promise<void> {
    if (employeeState().isAuthenticated && !employeeState().isLoading) {
      return
    }
    const persisted = readPersistedSession()
    if (persisted) {
      setEmployeeState({
        employee: persisted.employee,
        memberships: persisted.memberships,
        isAuthenticated: true,
        isLoading: false,
      })
      return
    }
    setEmployeeState({ employee: null, memberships: [], isAuthenticated: false, isLoading: false })
  },

  clear() {
    clearPersistedSession()
    setEmployeeState({ employee: null, memberships: [], isAuthenticated: false, isLoading: false })
  },
}

export async function resolveEmployeeRedirect(pathname: string): Promise<string | null> {
  await employeeStore.load()
  if (!employeeStore.isAuthenticated) {
    return `/app/employee/login?return=${encodeURIComponent(pathname)}`
  }
  return null
}
